import { useState, useEffect, Suspense } from "react"
import { getFunctions, connectFunctionsEmulator } from "firebase/functions"
import { getAuth, onAuthStateChanged, signInWithPopup, GoogleAuthProvider, User } from "firebase/auth"
import { getDatabase, ref, onValue } from "firebase/database"
import { initializeApp } from "firebase/app"
import { firebaseConfig } from "./firebaseConfig"
import { Button, ConfigProvider } from "antd"
import { httpsCallable } from "firebase/functions"
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom"
import locale from "antd/locale/de_DE"
import dayjs from "dayjs"
import "dayjs/locale/de"
import weekday from "dayjs/plugin/weekday"
import localeData from "dayjs/plugin/localeData"
import weekOfYear from "dayjs/plugin/weekOfYear"
import weekYear from "dayjs/plugin/weekYear"
import advancedFormat from "dayjs/plugin/advancedFormat"
import customParseFormat from "dayjs/plugin/customParseFormat"

dayjs.extend(customParseFormat)
dayjs.extend(advancedFormat)
dayjs.extend(weekday)
dayjs.extend(localeData)
dayjs.extend(weekOfYear)
dayjs.extend(weekYear)
dayjs.locale("de")

import BaseLayout from "./components/BaseLayout"
import ProtectedRoute from "./components/ProtectedRoute"
import PublicSite from "./landing/PublicSite"
import { isPublicSite } from "./siteMode"
import { AuthProvider, type AppPermissions } from "./authContext"
import { emailToRtdbKey, firstAllowedPage, permissionsFromAppUser, OPTIMIZATION_TAB_KEYS, runDetailPath, APP_PAGE_KEYS, HARVESTING_TABS, REPRICER_TABS } from "./pageAccess"
import { RoutedPageHub } from "./components/elements/PageHub"
import { AppUpdateWatcher } from "./components/elements/AppUpdateWatcher"
import {
  LazyAmzProducts,
  LazyAmzProductsHub,
  LazyAmzProductHistory,
  LazyAmzProductFees,
  LazyProductPlanning,
  LazyDashboard,
  LazyDayparting,
  LazyEvents,
  LazyForbidden,
  LazyHarvestingCandidates,
  LazyHarvestingRules,
  LazyHarvestingStudio,
  LazyKeywords,
  LazyTags,
  LazyListingAudit,
  LazyListingAuditDetail,
  LazyAccountHealth,
  LazyMarketplaces,
  LazyModes,
  LazyNegatives,
  LazyNotFound,
  LazyAssistant,
  LazyOptimization,
  LazyOrdersList,
  LazyPortfolios,
  LazyProductsList,
  LazyRepricerCandidates,
  LazyRepricerRules,
  LazyRepricerStudio,
  LazyRunDetail,
  LazyRuns,
  LazySettings,
  LazyMonitoring,
  LazyChangeHistory,
  LazyUsers,
  PageRouteFallback,
  PAGE_PATH_BY_KEY,
} from "./lazyPages"


// Initialize Firebase
const firebaseApp = initializeApp(firebaseConfig)
const functions = getFunctions(firebaseApp, "europe-west1")

if (process.env.NODE_ENV === "development") {
  connectFunctionsEmulator(functions, "127.0.0.1", 5001)
}

export { functions }

export const updateOrderFunc = httpsCallable(functions, "updateOrderFunc")
export const updateProductsAndSyncInventoriesFunc = httpsCallable(functions, "updateProductsAndSyncInventoriesFunc")
export const updateKFLPriceFunc = httpsCallable(functions, "updateKFLPriceFunc")
export const requestMCFFunc = httpsCallable(functions, "requestMCFFunc")
export const syncInventoriesSingleProductFunc = httpsCallable(functions, "syncInventoriesSingleProductFunc")
export const syncEanMappingsFunc = httpsCallable(functions, "syncEanMappingsFunc")
export const updateProductSkuFunc = httpsCallable(functions, "updateProductSkuFunc")
export const syncKauflandOrdersFunc = httpsCallable(functions, "syncKauflandOrdersFunc", { timeout: 540_000 })
export const getPortfoliosDashboardFunc = httpsCallable(functions, "getPortfoliosDashboardFunc")
export const updatePortfolioOptimizationActiveFunc = httpsCallable(functions, "updatePortfolioOptimizationActiveFunc")
export const getAsinDailyStatsFunc = httpsCallable(functions, "getAsinDailyStatsFunc")
export const getAmzProductsFunc = httpsCallable(functions, "getAmzProductsFunc")
export const getProductPlanningFunc = httpsCallable(functions, "getProductPlanningFunc", { timeout: 120_000 })
export const getProductMarketPlansForAsinFunc = httpsCallable(functions, "getProductMarketPlansForAsinFunc")
export const saveProductMarketPlanFunc = httpsCallable(functions, "saveProductMarketPlanFunc")
export const saveProductMarketPlansBulkFunc = httpsCallable(functions, "saveProductMarketPlansBulkFunc", { timeout: 120_000 })
export const getFbaTierFeeMatrixFunc = httpsCallable(functions, "getFbaTierFeeMatrixFunc")
export const saveAmzSkuFieldFunc = httpsCallable(functions, "saveAmzSkuFieldFunc")
export const saveAmzAsinFieldFunc = httpsCallable(functions, "saveAmzAsinFieldFunc")
export const saveAmzSettingFunc = httpsCallable(functions, "saveAmzSettingFunc")
export const getActiveMarketsFunc = httpsCallable(functions, "getActiveMarketsFunc")
export const syncAmzProductsAllFunc = httpsCallable(functions, "syncAmzProductsAllFunc", { timeout: 540_000 })
export const reconcileAmzSkuActiveStatusFunc = httpsCallable(functions, "reconcileAmzSkuActiveStatusFunc")
export const saveAmzPrimarySkuFunc = httpsCallable(functions, "saveAmzPrimarySkuFunc")
export const saveAmzAsinPlanningMonthlySalesFunc = httpsCallable(functions, "saveAmzAsinPlanningMonthlySalesFunc")
export const saveAmzAsinTrackerExcludedFunc = httpsCallable(functions, "saveAmzAsinTrackerExcludedFunc")
export const getAmzProductChangeLogFunc = httpsCallable(functions, "getAmzProductChangeLogFunc")
export const exportFbaFeeOverchargeFunc = httpsCallable(functions, "exportFbaFeeOverchargeFunc", { timeout: 300_000 })
export const checkListingApiAccessFunc = httpsCallable(functions, "checkListingApiAccessFunc")
export const pullListingContentFunc = httpsCallable(functions, "pullListingContentFunc", { timeout: 300_000 })
export const bulkPullListingsFunc = httpsCallable(functions, "bulkPullListingsFunc", { timeout: 540_000 })
export const saveListingDraftFunc = httpsCallable(functions, "saveListingDraftFunc")
export const clearListingDraftFunc = httpsCallable(functions, "clearListingDraftFunc")
export const validateListingPushFunc = httpsCallable(functions, "validateListingPushFunc", { timeout: 120_000 })
export const pushListingToAmazonFunc = httpsCallable(functions, "pushListingToAmazonFunc", { timeout: 120_000 })
export const getListingAuditSummaryFunc = httpsCallable(functions, "getListingAuditSummaryFunc")
export const translateListingIssueFunc = httpsCallable(functions, "translateListingIssueFunc", { timeout: 60_000 })
export const getServerStatsFunc = httpsCallable(functions, "getServerStatsFunc")
export const getDataHealthFunc = httpsCallable(functions, "getDataHealthFunc")
export const getJobRunHistoryFunc = httpsCallable(functions, "getJobRunHistoryFunc")
export const getChangeHistoryFunc = httpsCallable(functions, "getChangeHistoryFunc")
export const getPostgresFirewallRulesFunc = httpsCallable(functions, "getPostgresFirewallRulesFunc")
export const allowPostgresFirewallIpFunc = httpsCallable(functions, "allowPostgresFirewallIpFunc")
export const deletePostgresFirewallIpFunc = httpsCallable(functions, "deletePostgresFirewallIpFunc")
export const getModesFunc = httpsCallable(functions, "getModesFunc")
export const saveModeFunc = httpsCallable(functions, "saveModeFunc")
export const deleteModeFunc = httpsCallable(functions, "deleteModeFunc")
export const getKeywordModeAssignmentsFunc = httpsCallable(functions, "getKeywordModeAssignmentsFunc")
export const updateKeywordModesFunc = httpsCallable(functions, "updateKeywordModesFunc")
export const bulkAdjustKeywordBidsFunc = httpsCallable(functions, "bulkAdjustKeywordBidsFunc")
export const checkKeywordAmazonStatusFunc = httpsCallable(functions, "checkKeywordAmazonStatusFunc")
export const getEventsFunc = httpsCallable(functions, "getEventsFunc")
export const saveEventFunc = httpsCallable(functions, "saveEventFunc")
export const deleteEventFunc = httpsCallable(functions, "deleteEventFunc")
export const getTagsFunc = httpsCallable(functions, "getTagsFunc")
export const saveTagFunc = httpsCallable(functions, "saveTagFunc")
export const deleteTagFunc = httpsCallable(functions, "deleteTagFunc")
export const getMarketplacesFunc = httpsCallable(functions, "getMarketplacesFunc")
export const saveMarketplaceBidLimitsFunc = httpsCallable(functions, "saveMarketplaceBidLimitsFunc")
export const askAssistantFunc = httpsCallable(functions, "askAssistantFunc", { timeout: 300_000 })
export const getAssistantConfigFunc = httpsCallable(functions, "getAssistantConfigFunc")
export const listAssistantThreadsFunc = httpsCallable(functions, "listAssistantThreadsFunc")
export const getAssistantMessagesFunc = httpsCallable(functions, "getAssistantMessagesFunc")
export const createAssistantThreadFunc = httpsCallable(functions, "createAssistantThreadFunc")
export const getRunsFunc = httpsCallable(functions, "getRunsFunc")
export const getRunDetailFunc = httpsCallable(functions, "getRunDetailFunc")
export const getKeywordBidLogFunc = httpsCallable(functions, "getKeywordBidLogFunc")
export const getRunInsightFunc = httpsCallable(functions, "getRunInsightFunc", { timeout: 120_000 })
export const getRunChatFunc = httpsCallable(functions, "getRunChatFunc")
export const askRunChatFunc = httpsCallable(functions, "askRunChatFunc", { timeout: 300_000 })
export const getDaypartingOverviewFunc = httpsCallable(functions, "getDaypartingOverviewFunc")
export const saveDaypartingSettingsFunc = httpsCallable(functions, "saveDaypartingSettingsFunc")
export const getDaypartingActivityFunc = httpsCallable(functions, "getDaypartingActivityFunc")
export const daypartingRestoreNowFunc = httpsCallable(functions, "daypartingRestoreNowFunc", { timeout: 540_000 })
export const setDaypartingKillSwitchFunc = httpsCallable(functions, "setDaypartingKillSwitchFunc")
export const recomputeDaypartingCurvesFunc = httpsCallable(functions, "recomputeDaypartingCurvesFunc", { timeout: 540_000 })
export const getDaypartingHeatmapFunc = httpsCallable(functions, "getDaypartingHeatmapFunc", { timeout: 300_000 })
export const getHeatmapFilterOptionsFunc = httpsCallable(functions, "getHeatmapFilterOptionsFunc")
export const getPlacementComparisonFunc = httpsCallable(functions, "getPlacementComparisonFunc", { timeout: 300_000 })
export const getDaypartingSuggestionFunc = httpsCallable(functions, "getDaypartingSuggestionFunc", { timeout: 300_000 })
export const startListingAuditFunc = httpsCallable(functions, "startListingAuditFunc", { timeout: 540_000 })
export const getListingAuditRunsFunc = httpsCallable(functions, "getListingAuditRunsFunc")
export const getListingAuditRunDetailFunc = httpsCallable(functions, "getListingAuditRunDetailFunc")
export const syncReferenceFromDriveFunc = httpsCallable(functions, "syncReferenceFromDriveFunc", { timeout: 300_000 })
export const syncImageReferenceFromDriveFunc = httpsCallable(functions, "syncImageReferenceFromDriveFunc", { timeout: 540_000 })
export const importListingReferenceCsvFunc = httpsCallable(functions, "importListingReferenceCsvFunc")
export const getListingHealthInboxFunc = httpsCallable(functions, "getListingHealthInboxFunc")
export const getListingHealthCatalogFunc = httpsCallable(functions, "getListingHealthCatalogFunc")
export const getListingHealthSettingsFunc = httpsCallable(functions, "getListingHealthSettingsFunc")
export const saveListingHealthSettingsFunc = httpsCallable(functions, "saveListingHealthSettingsFunc")
export const saveListingHealthPriorityRuleFunc = httpsCallable(functions, "saveListingHealthPriorityRuleFunc")
export const saveListingHealthExpectedFamilyFunc = httpsCallable(functions, "saveListingHealthExpectedFamilyFunc")
export const updateListingHealthIssueFunc = httpsCallable(functions, "updateListingHealthIssueFunc")
export const createListingHealthIssueFunc = httpsCallable(functions, "createListingHealthIssueFunc")
export const getCompetitorWatchlistFunc = httpsCallable(functions, "getCompetitorWatchlistFunc")
export const saveCompetitorWatchlistFunc = httpsCallable(functions, "saveCompetitorWatchlistFunc")
export const deleteCompetitorWatchlistFunc = httpsCallable(functions, "deleteCompetitorWatchlistFunc")
export const getCompetitorHitsFunc = httpsCallable(functions, "getCompetitorHitsFunc")
export const updateCompetitorHitFunc = httpsCallable(functions, "updateCompetitorHitFunc")
export const checkAsinAgainstWatchlistFunc = httpsCallable(functions, "checkAsinAgainstWatchlistFunc", { timeout: 300_000 })
export const scanReplicatedAsinsForSellerFunc = httpsCallable(functions, "scanReplicatedAsinsForSellerFunc", { timeout: 540_000 })
export const scanSharedReviewsFunc = httpsCallable(functions, "scanSharedReviewsFunc", { timeout: 540_000 })
export const runBuyBoxMonitorFunc = httpsCallable(functions, "runBuyBoxMonitorFunc", { timeout: 540_000 })
export const getAccountHealthFunc = httpsCallable(functions, "getAccountHealthFunc")
export const refreshAccountHealthFunc = httpsCallable(functions, "refreshAccountHealthFunc", { timeout: 300_000 })
export const getHealthScanDigestsFunc = httpsCallable(functions, "getHealthScanDigestsFunc")
export const markHealthScanDigestReadFunc = httpsCallable(functions, "markHealthScanDigestReadFunc")
export const getHarvestingRulesFunc = httpsCallable(functions, "getHarvestingRulesFunc")
export const saveHarvestingRuleFunc = httpsCallable(functions, "saveHarvestingRuleFunc")
export const deleteHarvestingRuleFunc = httpsCallable(functions, "deleteHarvestingRuleFunc")
export const previewHarvestingRuleFunc = httpsCallable(functions, "previewHarvestingRuleFunc", { timeout: 120_000 })
export const runHarvestingRuleFunc = httpsCallable(functions, "runHarvestingRuleFunc", { timeout: 540_000 })
export const getHarvestingCandidatesFunc = httpsCallable(functions, "getHarvestingCandidatesFunc")
export const applyHarvestingCandidatesFunc = httpsCallable(functions, "applyHarvestingCandidatesFunc", { timeout: 540_000 })
export const dismissHarvestingCandidatesFunc = httpsCallable(functions, "dismissHarvestingCandidatesFunc")
export const deleteHarvestingCandidatesFunc = httpsCallable(functions, "deleteHarvestingCandidatesFunc")
export const getHarvestingOptionsFunc = httpsCallable(functions, "getHarvestingOptionsFunc")
export const getHarvestingAdGroupsFunc = httpsCallable(functions, "getHarvestingAdGroupsFunc")
export const getRepricerRulesFunc = httpsCallable(functions, "getRepricerRulesFunc")
export const saveRepricerRuleFunc = httpsCallable(functions, "saveRepricerRuleFunc")
export const deleteRepricerRuleFunc = httpsCallable(functions, "deleteRepricerRuleFunc")
export const previewRepricerRuleFunc = httpsCallable(functions, "previewRepricerRuleFunc", { timeout: 120_000 })
export const runRepricerRuleFunc = httpsCallable(functions, "runRepricerRuleFunc", { timeout: 540_000 })
export const getRepricerCandidatesFunc = httpsCallable(functions, "getRepricerCandidatesFunc")
export const applyRepricerCandidatesFunc = httpsCallable(functions, "applyRepricerCandidatesFunc", { timeout: 540_000 })
export const dismissRepricerCandidatesFunc = httpsCallable(functions, "dismissRepricerCandidatesFunc")
export const deleteRepricerCandidatesFunc = httpsCallable(functions, "deleteRepricerCandidatesFunc")
export const getRepricerOptionsFunc = httpsCallable(functions, "getRepricerOptionsFunc")
export const getRepricerSkuHistoryFunc = httpsCallable(functions, "getRepricerSkuHistoryFunc")
export const getNegativeCandidatesFunc = httpsCallable(functions, "getNegativeCandidatesFunc")
export const evaluateNegativesFunc = httpsCallable(functions, "evaluateNegativesFunc", { timeout: 540_000 })
export const applyNegativeCandidatesFunc = httpsCallable(functions, "applyNegativeCandidatesFunc")
export const dismissNegativeCandidatesFunc = httpsCallable(functions, "dismissNegativeCandidatesFunc")
export const deleteNegativeCandidatesFunc = httpsCallable(functions, "deleteNegativeCandidatesFunc")
export const getMyPermissionsFunc = httpsCallable(functions, "getMyPermissionsFunc")
export const getAppUsersFunc = httpsCallable(functions, "getAppUsersFunc")
export const saveAppUserFunc = httpsCallable(functions, "saveAppUserFunc")
export const deleteAppUserFunc = httpsCallable(functions, "deleteAppUserFunc")
export const submitFeedbackFunc = httpsCallable(functions, "submitFeedbackFunc")

const antdTheme = {
  token: {
    colorPrimary: "#1f8a6b",
    colorLink: "#0e5a45",
    colorSuccess: "#1f8a6b",
    colorError: "#b3261e",
    colorWarning: "#a07a00",
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontSize: 13,
    borderRadius: 4,
    colorBgContainer: "#ffffff",
    colorBgLayout: "#fafafa",
    colorBgElevated: "#ffffff",
    colorBorder: "#d8d8d8",
    colorBorderSecondary: "#ebebeb",
    colorText: "#1a1a1a",
    colorTextSecondary: "#6b6b6b",
    colorTextTertiary: "#9a9a9a",
    controlHeight: 30,
  },
  components: {
    Table: {
      headerBg: "#fafafa",
      headerColor: "#6b6b6b",
      rowHoverBg: "#fafafa",
      borderColor: "#ebebeb",
    },
    Card: {
      borderRadiusLG: 8,
    },
  },
}

const skipAuthInDev = process.env.NODE_ENV === "development"

const devPermissions: AppPermissions = {
  allowed: true,
  isAdmin: true,
  allowedPages: [ ...APP_PAGE_KEYS ],
}

const RedirectLegacyRunDetail = () => {
  const { date } = useParams<{ date: string }>()
  return <Navigate replace to={runDetailPath(date ?? "")} />
}

export const App = () => {
  const publicSite = isPublicSite()

  const [ user, setUser ] = useState<User | null>(null)
  const [ authReady, setAuthReady ] = useState(false)
  const [ permissions, setPermissions ] = useState<AppPermissions | null>(skipAuthInDev ? devPermissions : null)
  const [ permissionsReady, setPermissionsReady ] = useState(skipAuthInDev)
  const auth = getAuth()
  const isLoggedIn = user !== null
  const canUseApp = skipAuthInDev || (isLoggedIn && permissions?.allowed)

  useEffect(() => {
    if (publicSite) return
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser)
      setAuthReady(true)
    })
    return () => unsubscribe()
  }, [ auth, publicSite ])

  useEffect(() => {
    if (publicSite) return
    let robots = document.querySelector('meta[name="robots"]')
    if (!robots) {
      robots = document.createElement("meta")
      robots.setAttribute("name", "robots")
      document.head.appendChild(robots)
    }
    robots.setAttribute("content", "noindex, nofollow")
  }, [ publicSite ])

  useEffect(() => {
    if (publicSite) return
    const userEmail = user?.email
    if (!userEmail) {
      if (skipAuthInDev) {
        setPermissions(devPermissions)
        setPermissionsReady(true)
      } else {
        setPermissions(null)
        setPermissionsReady(true)
      }
      return
    }
    let cancelled = false
    setPermissionsReady(false)
    const db = getDatabase()
    const userKey = emailToRtdbKey(userEmail)

    const applyDenied = () => {
      setPermissions(skipAuthInDev ? devPermissions : { allowed: false, isAdmin: false, allowedPages: [] })
      setPermissionsReady(true)
    }

    const applyFromCallable = (data: AppPermissions & { status?: string; message?: string }) => {
      if (data.status === "error" || !data.allowed) {
        applyDenied()
        return
      }
      setPermissions(skipAuthInDev ? {
        allowed: true,
        isAdmin: Boolean(data.isAdmin),
        allowedPages: (data.allowedPages || []) as AppPermissions["allowedPages"],
        email: data.email || userEmail,
        displayName: data.displayName || user.displayName || undefined,
      } : {
        allowed: true,
        isAdmin: Boolean(data.isAdmin),
        allowedPages: (data.allowedPages || []) as AppPermissions["allowedPages"],
        email: data.email,
        displayName: data.displayName,
      })
      setPermissionsReady(true)
    }

    const applyRow = (row: { is_admin?: boolean; allowed_pages?: string[]; email?: string; display_name?: string | null }) => {
      const fromRow = permissionsFromAppUser(row, userEmail)
      setPermissions(skipAuthInDev ? {
        allowed: true,
        isAdmin: fromRow.isAdmin,
        allowedPages: fromRow.allowedPages,
        email: fromRow.email || userEmail,
        displayName: fromRow.displayName || user.displayName || undefined,
      } : fromRow)
      setPermissionsReady(true)
    }

    let calledFallback = false
    const fallbackToCallable = () => {
      if (calledFallback) return
      calledFallback = true
      getMyPermissionsFunc()
        .then(res => {
          if (!cancelled) applyFromCallable(res.data as AppPermissions & { status?: string; message?: string })
        })
        .catch(() => {
          if (!cancelled) applyDenied()
        })
    }

    const unsub = onValue(ref(db, `app_users/${userKey}`), snap => {
      if (cancelled) return
      if (snap.exists()) {
        applyRow(snap.val())
        return
      }
      fallbackToCallable()
    }, err => {
      console.error("[App] app_users subscription failed:", err)
      if (cancelled) return
      fallbackToCallable()
    })

    return () => {
      cancelled = true
      unsub()
    }
  }, [ publicSite, user ])


  if (publicSite) {
    return <PublicSite />
  }

  const handleLogin = async () => {
    try {
      const provider = new GoogleAuthProvider()
      const result = await signInWithPopup(auth, provider) // Switch from signInWithRedirect
      setUser(result.user) // Save the user after login
    } catch (error) {
      console.error("Login error:", error)
    }
  }


  if (!authReady && !skipAuthInDev) {
    return (
      <ConfigProvider locale={locale} theme={antdTheme}>
        <div className="auth-checking" aria-busy="true" aria-label="Checking sign-in" />
      </ConfigProvider>
    )
  }

  if (!skipAuthInDev && isLoggedIn && !permissionsReady) {
    return (
      <ConfigProvider locale={locale} theme={antdTheme}>
        <div className="auth-checking" aria-busy="true" aria-label="Loading permissions" />
      </ConfigProvider>
    )
  }

  if (!canUseApp) {
    return (
      <ConfigProvider locale={locale} theme={antdTheme}>
        <div className="login-screen">
          <div className="login-card">
            <div className="brand-row">
              <img src={`${process.env.PUBLIC_URL}/logo_viridian.png`} alt="" className="brand-mark" aria-hidden />
              <span style={{ fontWeight: 700, fontSize: 15 }}>Viridian</span>
            </div>
            <h1>Sign in</h1>
            <p>
              {isLoggedIn && permissions && !permissions.allowed
                ? "Your account is not authorized for this app. Contact an admin to get access."
                : "Use your Google account to access internal operations."}
            </p>
            <Button type="primary" size="large" block onClick={handleLogin}>
              Continue with Google
            </Button>
          </div>
        </div>
      </ConfigProvider>
    )
  }

  const activePermissions = permissions ?? devPermissions
  const homePage = firstAllowedPage(activePermissions.allowedPages)

  return (
    <ConfigProvider
      locale={locale}
      theme={antdTheme}
    >
      <AuthProvider value={activePermissions}>
      <BrowserRouter>
        <AppUpdateWatcher />
        <Suspense fallback={<PageRouteFallback />}>
          <Routes>
            <Route path="/" element={<BaseLayout />}>
              <Route path="" element={<Navigate replace to={PAGE_PATH_BY_KEY[homePage]} />} />
              <Route path="assistant" element={<ProtectedRoute pageKey="assistant"><LazyAssistant /></ProtectedRoute>} />
              <Route path="assistant/:threadId" element={<ProtectedRoute pageKey="assistant"><LazyAssistant /></ProtectedRoute>} />
              <Route path="orders" element={<ProtectedRoute pageKey="orders"><LazyOrdersList /></ProtectedRoute>} />
              <Route path="products" element={<ProtectedRoute pageKey="products"><LazyProductsList /></ProtectedRoute>} />
              <Route path="amz-products" element={<ProtectedRoute pageKey="amz-products"><LazyAmzProductsHub /></ProtectedRoute>}>
                <Route index element={<LazyAmzProducts />} />
                <Route path="history" element={<LazyAmzProductHistory />} />
                <Route path="fees" element={<LazyAmzProductFees />} />
                <Route path=":sku/:tab?" element={<LazyAmzProducts />} />
              </Route>
              <Route path="amz-history" element={<Navigate replace to="/amz-products/history" />} />
              <Route path="product-planning" element={<ProtectedRoute pageKey="product-planning"><LazyProductPlanning /></ProtectedRoute>} />
              <Route path="dashboard" element={<ProtectedRoute pageKey="dashboard"><LazyDashboard /></ProtectedRoute>} />
              <Route path="optimization" element={
                <ProtectedRoute pageKeys={[ ...OPTIMIZATION_TAB_KEYS ]}>
                  <LazyOptimization />
                </ProtectedRoute>
              }>
                <Route index element={<ProtectedRoute pageKey="runs"><LazyRuns /></ProtectedRoute>} />
                <Route path="keywords" element={<ProtectedRoute pageKey="keywords"><LazyKeywords /></ProtectedRoute>} />
                <Route path="tags" element={<ProtectedRoute pageKey="tags"><LazyTags /></ProtectedRoute>} />
                <Route path="modes" element={<ProtectedRoute pageKey="modes"><LazyModes /></ProtectedRoute>} />
                <Route path="events" element={<ProtectedRoute pageKey="events"><LazyEvents /></ProtectedRoute>} />
                <Route path="portfolios" element={<ProtectedRoute pageKey="portfolios"><LazyPortfolios /></ProtectedRoute>} />
                <Route path="marketplaces" element={<ProtectedRoute pageKey="marketplaces"><LazyMarketplaces /></ProtectedRoute>} />
                <Route path="runs/:date" element={<ProtectedRoute pageKey="runs"><LazyRunDetail /></ProtectedRoute>} />
              </Route>
              <Route path="mods" element={<Navigate replace to="/optimization/modes" />} />
              <Route path="modes" element={<Navigate replace to="/optimization/modes" />} />
              <Route path="events" element={<Navigate replace to="/optimization/events" />} />
              <Route path="keywords" element={<Navigate replace to="/optimization/keywords" />} />
              <Route path="tags" element={<Navigate replace to="/optimization/tags" />} />
              <Route path="portfolios" element={<Navigate replace to="/optimization/portfolios" />} />
              <Route path="marketplaces" element={<Navigate replace to="/optimization/marketplaces" />} />
              <Route path="runs" element={<Navigate replace to="/optimization" />} />
              <Route path="runs/:date" element={<RedirectLegacyRunDetail />} />
              <Route path="dayparting" element={<ProtectedRoute pageKey="dayparting"><LazyDayparting /></ProtectedRoute>} />
              <Route path="listing-audit" element={<ProtectedRoute pageKey="listing-audit"><LazyListingAudit /></ProtectedRoute>} />
              <Route path="listing-audit/run/:runId" element={<ProtectedRoute pageKey="listing-audit"><LazyListingAuditDetail /></ProtectedRoute>} />
              <Route path="listing-audit/:sku" element={<ProtectedRoute pageKey="listing-audit"><LazyListingAudit /></ProtectedRoute>} />
              <Route path="account-health" element={<ProtectedRoute pageKey="account-health"><LazyAccountHealth /></ProtectedRoute>} />
              <Route path="negatives" element={<ProtectedRoute pageKey="negatives"><LazyNegatives /></ProtectedRoute>} />
              <Route path="harvesting" element={<ProtectedRoute pageKey="harvesting"><RoutedPageHub tabs={HARVESTING_TABS} /></ProtectedRoute>}>
                <Route index element={<LazyHarvestingRules />} />
                <Route path="candidates" element={<LazyHarvestingCandidates />} />
              </Route>
              <Route path="harvesting/rules/new" element={<ProtectedRoute pageKey="harvesting"><LazyHarvestingStudio /></ProtectedRoute>} />
              <Route path="harvesting/rules/:id" element={<ProtectedRoute pageKey="harvesting"><LazyHarvestingStudio /></ProtectedRoute>} />
              <Route path="bid-optimization" element={<Navigate replace to="/optimization/portfolios" />} />
              <Route path="repricer" element={<ProtectedRoute pageKey="repricer"><RoutedPageHub tabs={REPRICER_TABS} /></ProtectedRoute>}>
                <Route index element={<LazyRepricerRules />} />
                <Route path="candidates" element={<LazyRepricerCandidates />} />
              </Route>
              <Route path="repricer/rules/new" element={<ProtectedRoute pageKey="repricer"><LazyRepricerStudio /></ProtectedRoute>} />
              <Route path="repricer/rules/:id" element={<ProtectedRoute pageKey="repricer"><LazyRepricerStudio /></ProtectedRoute>} />
              <Route path="keyword-modes" element={<Navigate replace to="/optimization/keywords" />} />
              <Route path="settings" element={<ProtectedRoute pageKey="settings"><LazySettings /></ProtectedRoute>} />
              <Route path="monitoring" element={<ProtectedRoute pageKey="settings"><LazyMonitoring /></ProtectedRoute>} />
              <Route path="history" element={<ProtectedRoute pageKey="history"><LazyChangeHistory /></ProtectedRoute>} />
              <Route path="users" element={<ProtectedRoute pageKey="users"><LazyUsers /></ProtectedRoute>} />
              <Route path="forbidden" element={<LazyForbidden />} />
              <Route path="*" element={<LazyNotFound />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
      </AuthProvider>
    </ConfigProvider>
  )
}
